import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, BellOff, BellRing, ChevronRight, CloudRain, Package, Receipt, Users, Wheat, X } from "lucide-react";
import { api } from "../../lib/api";

const TYPE_ICON = { cheque: Receipt, stock: Package, salary: Users, harvest: Wheat, weather: CloudRain };
const SEV_LABEL = { high: "Acil", medium: "Yakında", low: "Bilgi" };
const SEEN_KEY = "kn_alerts_seen_ids";

function loadSeen() {
  try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
  catch { return new Set(); }
}
function saveSeen(set) {
  try { localStorage.setItem(SEEN_KEY, JSON.stringify(Array.from(set))); } catch {}
}

export default function AlertsBell() {
  const [alerts, setAlerts] = useState({ items: [], counts: { total: 0, high: 0, medium: 0, low: 0 } });
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const notifiedRef = useRef(loadSeen());

  const load = async () => {
    try {
      const d = await api.get("/alerts");
      setAlerts(d);
      if (permission === "granted") {
        for (const item of d.items) {
          if (item.severity !== "high") continue;
          if (notifiedRef.current.has(item.id)) continue;
          try {
            new Notification("Kemal Namlı Çiftliği", {
              body: `${item.title} — ${item.subtitle}`,
              tag: item.id,
              icon: "/manifest.json",
            });
          } catch {}
          notifiedRef.current.add(item.id);
        }
        saveSeen(notifiedRef.current);
      }
    } catch {}
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return;
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") {
        new Notification("Bildirimler açıldı", { body: "Acil uyarılar burada da görünecek." });
      }
    } catch {}
  };

  const count = alerts.counts.total;
  const highCount = alerts.counts.high;

  return (
    <>
      <button className="notification alerts-bell" data-testid="alerts-bell" onClick={() => setOpen(true)} aria-label="Uyarılar">
        {highCount > 0 ? <BellRing size={19} /> : <Bell size={19} />}
        {count > 0 && <i data-testid="alerts-bell-count">{count > 99 ? "99+" : count}</i>}
      </button>

      {open && (
        <div className="alerts-drawer-backdrop" data-testid="alerts-drawer" onClick={() => setOpen(false)}>
          <div className="alerts-drawer" onClick={(e) => e.stopPropagation()}>
            <div className="alerts-drawer-head">
              <div>
                <span className="eyebrow"><span></span> UYARILAR</span>
                <h2>Hatırlatmalar</h2>
                <p>Vadesi yaklaşan çekler, maaş günleri, düşük stok ve hasat zamanı burada birleşir.</p>
              </div>
              <button className="icon-btn" data-testid="alerts-drawer-close" onClick={() => setOpen(false)}><X size={19} /></button>
            </div>

            {permission !== "granted" && (
              <div className="alerts-perm">
                {permission === "denied" ? (
                  <>
                    <BellOff size={16} />
                    <div>
                      <b>Tarayıcı bildirimleri kapalı</b>
                      <small>Ayarlar → Bildirimler'den bu siteye izin verin. iPhone'da uygulamayı ana ekrana ekledikten sonra ilk açılışta izin sorulur.</small>
                    </div>
                  </>
                ) : (
                  <>
                    <BellRing size={16} />
                    <div>
                      <b>Bildirim izni ver</b>
                      <small>Acil uyarılar (vadesi geçmiş çek, düşük stok, maaş günü) tarayıcı bildirimi olarak da gösterilsin.</small>
                    </div>
                    <button className="primary-btn" data-testid="alerts-perm-enable" onClick={requestPermission}>
                      İzin ver
                    </button>
                  </>
                )}
              </div>
            )}

            <div className="alerts-list">
              {alerts.items.length === 0 ? (
                <div className="alerts-empty">
                  <Bell size={26} />
                  <b>Şu an bekleyen uyarı yok</b>
                  <small>Her şey yolunda görünüyor.</small>
                </div>
              ) : (
                alerts.items.map((it) => {
                  const Icon = TYPE_ICON[it.type] || Bell;
                  return (
                    <Link
                      key={it.id}
                      to={it.link || "/"}
                      className={`alert-row sev-${it.severity}`}
                      data-testid={`alert-item-${it.id}`}
                      onClick={() => setOpen(false)}
                    >
                      <div className={`alert-row-icon sev-${it.severity}`}><Icon size={17} /></div>
                      <div className="alert-row-body">
                        <b>{it.title}</b>
                        <small>{it.subtitle}</small>
                      </div>
                      <span className={`alert-row-sev sev-${it.severity}`}>{SEV_LABEL[it.severity]}</span>
                      <ChevronRight size={15} />
                    </Link>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
